import {
  CustomToastSuccessMessage,
  DefaultToastMessages,
  type ToastProps,
} from '@components/primitives/Toasts/Toast'
import { trackEvent } from '@lib/tracking'
import type { TrackingEvents } from '@lib/tracking/TrackingEvents'

export interface ShareData {
  title: string
  url: string
}

export interface ShareUtilsProps {
  shareData: ShareData
  showToast: (toastSettings: ToastProps | null) => void
  toastDuration?: number
  trackingEvents: {
    shareAborted: TrackingEvents
    shareClicked: TrackingEvents
    shareSuccess: TrackingEvents
  }
}

export const handleNativeShare = async ({
  shareData,
  showToast,
  toastDuration = 2000,
  trackingEvents,
}: ShareUtilsProps): Promise<void> => {
  trackEvent(trackingEvents.shareClicked)

  if (navigator.share) {
    try {
      await navigator.share(shareData)
      trackEvent(trackingEvents.shareSuccess)
      showToast({ ...DefaultToastMessages.success, duration: toastDuration })
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          trackEvent(trackingEvents.shareAborted)
          await navigator.clipboard.writeText(shareData.url)
          showToast({
            ...DefaultToastMessages.success,
            content: (
              <CustomToastSuccessMessage message='Copied to clipboard' />
            ),
            duration: toastDuration,
          })
        } else {
          await navigator.clipboard.writeText(shareData.url)
          showToast({
            ...DefaultToastMessages.copy,
            duration: toastDuration,
          })
        }
      } else {
        await navigator.clipboard.writeText(shareData.url)
        showToast({
          ...DefaultToastMessages.copy,
          duration: toastDuration,
        })
      }
    }
  } else {
    // Fallback if the browser or device does not support the share API
    try {
      await navigator.clipboard.writeText(shareData.url)
      showToast({ ...DefaultToastMessages.copy, duration: toastDuration })
    } catch (error) {
      showToast({ ...DefaultToastMessages.failure, duration: toastDuration })
    }
  }
}
